import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type { IAiSpreadsheetRegion } from "@/payroll/ai-script/types";
import { SpreadsheetColumnFilterEditor } from "./spreadsheet-column-filter-editor";
import {
  SPREADSHEET_REGION_MIXED_COLUMN_TYPE,
  hasActiveSpreadsheetColumnFilter,
  type SpreadsheetColumnFilter,
  type SpreadsheetRegionColumnType,
  type SpreadsheetRegionViewState,
} from "./spreadsheet-region-view";
import { VIEWER_OVERLAY_CLASSNAME } from "./utils";

export interface SpreadsheetColumnMenuTarget {
  x: number;
  y: number;
  column: number;
  region: IAiSpreadsheetRegion;
  values: string[];
  columnType: SpreadsheetRegionColumnType;
  /** The column's own header text, so the menu can name what it is editing. */
  label: string;
  /** Let Radix move focus into the menu when the grid opened it by keyboard. */
  focusOnOpen?: boolean;
}

interface SpreadsheetColumnMenuProps {
  target: SpreadsheetColumnMenuTarget | null;
  viewState: SpreadsheetRegionViewState | undefined;
  onClose: () => void;
  onFocusReturn: () => void;
  onChange: (state: SpreadsheetRegionViewState) => void;
}

interface SpreadsheetColumnMenuEditorProps
  extends Omit<SpreadsheetColumnMenuProps, "target" | "viewState"> {
  target: SpreadsheetColumnMenuTarget;
  state: SpreadsheetRegionViewState;
  committedFilter: SpreadsheetColumnFilter;
}

const EMPTY_COLUMN_FILTER: SpreadsheetColumnFilter = {
  selectedValues: null,
  condition: null,
  conditionValue: "",
};

const filtersAreEqual = (
  left: SpreadsheetColumnFilter,
  right: SpreadsheetColumnFilter,
) =>
  left.condition === right.condition &&
  left.conditionValue === right.conditionValue &&
  (left.selectedValues === right.selectedValues ||
    (left.selectedValues !== null &&
      right.selectedValues !== null &&
      left.selectedValues.length === right.selectedValues.length &&
      left.selectedValues.every(
        (value, index) => value === right.selectedValues?.[index],
      )));

const describeActiveState = (
  state: SpreadsheetRegionViewState,
  column: number,
): string | null => {
  const filter = state.filters[String(column)];
  const parts: string[] = [];

  if (
    !hasActiveSpreadsheetColumnFilter(filter) &&
    state.sort?.column !== column
  ) {
    return null;
  }

  if (filter?.selectedValues !== null && filter?.selectedValues !== undefined) {
    parts.push(`${filter.selectedValues.length} values kept`);
  }
  if (filter?.condition && filter.conditionValue !== "") {
    parts.push("condition applied");
  }
  if (state.sort?.column === column) {
    parts.push(state.sort.direction === "asc" ? "sorted A–Z" : "sorted Z–A");
  }

  return parts.length > 0 ? parts.join(" · ") : null;
};

const SpreadsheetColumnMenuEditor = ({
  target,
  state,
  committedFilter,
  onClose,
  onFocusReturn,
  onChange,
}: SpreadsheetColumnMenuEditorProps) => {
  const columnKey = String(target.column);
  const setting = target.region.column_settings[columnKey];
  const activeSummary = describeActiveState(state, target.column);

  /**
   * Building a filter is many small decisions — a dozen checkboxes, then a
   * condition — and re-projecting the sheet after each one is wasted work the
   * user waits through on a large tab. So the filter is edited as a local draft
   * and only committed on Apply — or by any other commit, such as sorting.
   */
  const [draft, setDraft] = useState<SpreadsheetColumnFilter>(committedFilter);

  const isDirty =
    !filtersAreEqual(draft, committedFilter) &&
    // An untouched column starts with everything selected; picking every value
    // by hand is the same view, so do not call that a pending change.
    !(
      draft.selectedValues !== null &&
      committedFilter.selectedValues === null &&
      draft.selectedValues.length === target.values.length &&
      draft.condition === committedFilter.condition &&
      draft.conditionValue === committedFilter.conditionValue
    );

  /**
   * Single commit path. Every change carries the pending filter draft with it, so
   * sorting with checkboxes half-chosen applies them rather than throwing them
   * away — the draft exists to batch the work, not to be quietly lost.
   */
  const commit = (changes: Partial<SpreadsheetRegionViewState>) => {
    const filters = { ...state.filters };
    if (hasActiveSpreadsheetColumnFilter(draft)) filters[columnKey] = draft;
    else delete filters[columnKey];
    onChange({ ...state, filters, ...changes });
  };

  const applyDraft = () => {
    commit({});
    onClose();
  };

  const isSortedAscending =
    state.sort?.column === target.column && state.sort.direction === "asc";
  const isSortedDescending =
    state.sort?.column === target.column && state.sort.direction === "desc";

  return (
    <Popover
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverTrigger asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed h-0 w-0"
          style={{ left: target.x, top: target.y }}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // A pointer-opened menu comes from the grid canvas, not this hidden
        // trigger, and Glide takes focus back after the click. Keep that hand-off
        // from closing the menu. A keyboard-opened menu permits Radix autofocus
        // so the user can move through its controls. Pointer-down outside stays
        // active, so a click elsewhere on the sheet still closes the menu.
        onOpenAutoFocus={(event) => {
          if (!target.focusOnOpen) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (target.focusOnOpen) onFocusReturn();
        }}
        onFocusOutside={(event) => {
          if (!target.focusOnOpen) event.preventDefault();
        }}
        collisionPadding={12}
        /**
         * Padding is applied per section rather than on the popover, so the rules
         * between sections reach both edges instead of floating in a gutter.
         * `overflow-hidden` matters: the popover itself must never scroll, or the
         * value list's own scrollbar sits next to a second one for the whole menu.
         */
        className={cn(
          "flex max-h-[var(--radix-popover-content-available-height)] w-[19.5rem] flex-col overflow-hidden p-0",
          VIEWER_OVERLAY_CLASSNAME,
        )}
      >
        <Stack gap="none" fullWidth className="shrink-0 px-4 pb-3 pt-3.5">
          {/* Name the column being configured. "Column options" over a grid of
              twenty-one look-alike headers tells you nothing about which one the
              menu belongs to. */}
          <Typography variant="subtitleW3" className="truncate">
            {target.label || `Column ${target.column}`}
          </Typography>
          {/* Green: this line reports a control the user has actively applied,
              so it takes the interactive colour. It was `text-accent`, one of
              the shadcn aliases never wired to Volca's palette — a near-white
              #F5F5F5 that rendered at 1.09:1 on this popover, i.e. invisible. */}
          {activeSummary && (
            <Typography variant="bodyW3" textColor="interactive">
              {activeSummary}
            </Typography>
          )}
          {target.columnType === SPREADSHEET_REGION_MIXED_COLUMN_TYPE && (
            <Typography
              variant="bodyW3"
              textColor="secondary"
              className="pt-1 leading-snug"
            >
              Holds more than one kind of value, so it sorts and compares as
              text.
            </Typography>
          )}
        </Stack>

        {setting?.sortable && (
          <div className="grid shrink-0 grid-cols-2 gap-2 px-4 pb-3">
            {/* The direction already applied reads as pressed, so the menu shows
                the current state instead of only offering to change it. */}
            <Button
              variant={isSortedAscending ? "secondary" : "tertiary"}
              size="xs"
              aria-pressed={isSortedAscending}
              leftIcon={<BarsArrowUpIcon className="h-4 w-4" />}
              onClick={() =>
                commit({ sort: { column: target.column, direction: "asc" } })
              }
            >
              Sort A to Z
            </Button>
            <Button
              variant={isSortedDescending ? "secondary" : "tertiary"}
              size="xs"
              aria-pressed={isSortedDescending}
              leftIcon={<BarsArrowDownIcon className="h-4 w-4" />}
              onClick={() =>
                commit({ sort: { column: target.column, direction: "desc" } })
              }
            >
              Sort Z to A
            </Button>
          </div>
        )}

        {setting?.filterable && (
          <SpreadsheetColumnFilterEditor
            values={target.values}
            filter={draft}
            onChange={setDraft}
          />
        )}

        <Stack
          direction="row"
          justify="between"
          align="center"
          gap="sm"
          fullWidth
          className="shrink-0 border-t border-stroke-tertiary px-4 py-3"
        >
          <Button
            variant="text"
            size="xs"
            className="h-auto p-0"
            onClick={() => {
              const filters = { ...state.filters };
              delete filters[columnKey];
              onChange({
                ...state,
                filters,
                sort: state.sort?.column === target.column ? null : state.sort,
              });
              onClose();
            }}
          >
            Clear column
          </Button>
          <Stack direction="row" align="center" gap="sm">
            {setting?.filterable ? (
              <>
                <Button variant="tertiary" size="xs" onClick={onClose}>
                  Cancel
                </Button>
                {/* Applying is the only path that re-projects the sheet, so on a
                    big tab the user pays that cost once instead of per checkbox. */}
                <Button size="xs" onClick={isDirty ? applyDraft : onClose}>
                  Apply
                </Button>
              </>
            ) : (
              <Button variant="tertiary" size="xs" onClick={onClose}>
                Done
              </Button>
            )}
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};

export const SpreadsheetColumnMenu = ({
  target,
  viewState,
  onClose,
  onFocusReturn,
  onChange,
}: SpreadsheetColumnMenuProps) => {
  if (!target) return null;

  const state = viewState ?? { filters: {}, sort: null };
  const committedFilter =
    state.filters[String(target.column)] ?? EMPTY_COLUMN_FILTER;

  return (
    <SpreadsheetColumnMenuEditor
      // A new editor instance owns each committed filter. This resets the local
      // draft without an effect or a state update during render.
      key={`${target.region.id}:${target.column}:${JSON.stringify(committedFilter)}`}
      target={target}
      state={state}
      committedFilter={committedFilter}
      onClose={onClose}
      onFocusReturn={onFocusReturn}
      onChange={onChange}
    />
  );
};
